import { useEffect, useRef, useState } from "react";
import { FaArrowLeft, FaEnvelope, FaEye, FaEyeSlash, FaLock } from "react-icons/fa";
import { api, type AuthState } from "../api.ts";

function Shell({ title, subtitle, children }: { title: string; subtitle: string; children: React.ReactNode }) {
  return <div className="auth-wrap">
    <div className="auth-ambient auth-ambient-one"/><div className="auth-ambient auth-ambient-two"/>
    <section className="auth-brand-panel">
      <a href="/" className="brand">Signals</a>
      <div className="auth-brand-copy"><div className="pill"><i/> Recognition that never calls home</div>
        <h2>Know what’s playing.<br/><em>Anywhere.</em></h2>
        <p>Build private audio-recognition catalogs and ship matching directly to the edge.</p>
      </div>
      <div className="auth-wave" aria-hidden="true">{[18,32,52,26,64,82,44,30,70,48,88,60,34,72,50,22,54,78,40,20,48,66,36,16].map((h,i)=><i key={i} style={{height:h}}/>)}</div>
      <div className="auth-trust"><span><b>100%</b> offline matching</span><span><b>16 kHz</b> native pipeline</span></div>
    </section>
    <section className="auth-form-panel"><a href="/" className="brand auth-mobile-brand">Signals</a><div className="auth-card">
      <div className="eyebrow">ACR CLOUD</div><h1>{title}</h1><p className="muted">{subtitle}</p>{children}
    </div></section>
  </div>;
}
function RecoveryShell({ title, subtitle, icon, children }: { title: string; subtitle: string; icon: "email" | "lock"; children: React.ReactNode }) {
  return <div className="recovery-wrap"><div className="recovery-grid"/><div className="recovery-glow"/>
    <a href="/" className="brand recovery-brand">Signals</a>
    <main className="recovery-card">
      <a className="recovery-back" href="#/login"><FaArrowLeft/> Back to sign in</a>
      <div className="recovery-icon">{icon === "email" ? <FaEnvelope/> : <FaLock/>}</div>
      <div className="eyebrow">ACCOUNT RECOVERY</div><h1>{title}</h1><p className="muted">{subtitle}</p>
      {children}
      <div className="recovery-note"><FaLock/> Your reset link is encrypted and expires after one hour.</div>
    </main>
  </div>;
}
function Field({ label, ...props }: React.InputHTMLAttributes<HTMLInputElement> & { label: string }) {
  return <label className="field"><span>{label}</span><input {...props} /></label>;
}
function PasswordField({ label, ...props }: Omit<React.InputHTMLAttributes<HTMLInputElement>, "type"> & { label: string }) {
  const [visible, setVisible] = useState(false);
  return <label className="field"><span>{label}</span><div className="password-input">
    <input {...props} type={visible ? "text" : "password"} />
    <button type="button" onClick={() => setVisible(v => !v)} aria-label={visible ? "Hide password" : "Show password"} title={visible ? "Hide password" : "Show password"}>
      {visible ? <FaEyeSlash/> : <FaEye/>}
    </button>
  </div></label>;
}

declare global {
  interface Window {
    google?: { accounts: { id: {
      initialize(options: { client_id: string; callback: (response: { credential: string }) => void }): void;
      renderButton(element: HTMLElement, options: Record<string, unknown>): void;
    } } };
  }
}
let googleScript: Promise<void> | null = null;
function loadGoogle(): Promise<void> {
  if (window.google) return Promise.resolve();
  if (!googleScript) googleScript = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Could not load Google sign-in"));
    document.head.appendChild(script);
  });
  return googleScript;
}
function GoogleButton({ onAuth, onError }: { onAuth: (state: AuthState) => void; onError: (error: string) => void }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    let active = true;
    let observer: ResizeObserver | null = null;
    Promise.all([loadGoogle(), fetch("/api/config").then(r => r.json())]).then(([, config]) => {
      if (!active || !ref.current || !config.googleClientId || !window.google) return;
      window.google.accounts.id.initialize({
        client_id: config.googleClientId,
        callback: async ({ credential }) => {
          try { onAuth(await api.google(credential)); }
          catch (e) { onError((e as Error).message); }
        },
      });
      // Google's button takes a fixed pixel width, so match it to the card's
      // inner width (the same width as the form fields below) and re-render
      // when that changes, e.g. crossing the mobile breakpoint. Max 400 is
      // the widest the GSI button allows.
      const renderButton = () => {
        if (!ref.current || !window.google) return;
        const width = Math.min(400, Math.max(200, Math.round(ref.current.clientWidth) || 358));
        ref.current.innerHTML = "";
        window.google.accounts.id.renderButton(ref.current, {
          theme: "filled_black", size: "large", shape: "rectangular", width, text: "continue_with",
        });
      };
      renderButton();
      if (typeof ResizeObserver !== "undefined") {
        let lastWidth = ref.current.clientWidth;
        observer = new ResizeObserver(() => {
          const width = ref.current?.clientWidth ?? 0;
          if (Math.abs(width - lastWidth) > 4) { lastWidth = width; renderButton(); }
        });
        observer.observe(ref.current);
      }
    }).catch(e => onError((e as Error).message));
    return () => { active = false; observer?.disconnect(); };
  }, [onAuth, onError]);
  return <><div className="google-button" ref={ref}/><div className="auth-divider"><span>or continue with email</span></div></>;
}

export function Login({ onAuth }: { onAuth: (state: AuthState) => void }) {
  const [email,setEmail]=useState(""),[password,setPassword]=useState(""),[error,setError]=useState(""),[busy,setBusy]=useState(false);
  const submit=async(e:React.FormEvent)=>{e.preventDefault();setBusy(true);setError("");try{onAuth(await api.login(email,password));}catch(e){setError((e as Error).message)}finally{setBusy(false)}};
  return <Shell title="Welcome back" subtitle="Sign in to manage your recognition catalog."><GoogleButton onAuth={onAuth} onError={setError}/><form onSubmit={submit} className="auth-form">
    <Field label="Work email" type="email" value={email} onChange={e=>setEmail(e.target.value)} required autoFocus />
    <div><PasswordField label="Password" value={password} onChange={e=>setPassword(e.target.value)} required /><a className="field-link" href="#/forgot-password">Forgot password?</a></div>
    {error&&<p className="form-error">{error}</p>}<button className="primary wide" disabled={busy}>{busy?"Signing in…":"Sign in"}</button>
  </form><p className="auth-switch">New to Signals? <a href="#/signup">Create an account</a></p></Shell>;
}
export function Signup({ onAuth }: { onAuth: (state: AuthState) => void }) {
  const [name,setName]=useState(""),[email,setEmail]=useState(""),[password,setPassword]=useState(""),[error,setError]=useState(""),[busy,setBusy]=useState(false);
  const submit=async(e:React.FormEvent)=>{e.preventDefault();setBusy(true);setError("");try{onAuth(await api.signup(name,email,password));}catch(e){setError((e as Error).message)}finally{setBusy(false)}};
  return <Shell title="Create your workspace" subtitle="Start building an offline-ready audio catalog."><GoogleButton onAuth={onAuth} onError={setError}/><form onSubmit={submit} className="auth-form">
    <Field label="Full name" value={name} onChange={e=>setName(e.target.value)} required autoFocus /><Field label="Work email" type="email" value={email} onChange={e=>setEmail(e.target.value)} required />
    <PasswordField label="Password" minLength={8} value={password} onChange={e=>setPassword(e.target.value)} required placeholder="8 characters minimum" />
    {error&&<p className="form-error">{error}</p>}<button className="primary wide" disabled={busy}>{busy?"Creating account…":"Create account"}</button>
  </form><p className="auth-switch">Already have an account? <a href="#/login">Sign in</a></p></Shell>;
}
export function ForgotPassword() {
  const [email,setEmail]=useState(""),[sent,setSent]=useState(false),[busy,setBusy]=useState(false);
  const submit=async(e:React.FormEvent)=>{e.preventDefault();setBusy(true);await api.forgotPassword(email);setSent(true);setBusy(false)};
  return <RecoveryShell icon="email" title={sent ? "Check your inbox" : "Forgot your password?"} subtitle={sent ? "We’ve sent recovery instructions if that address belongs to an account." : "Enter the email associated with your account and we’ll send you a secure reset link."}>{sent?<div className="recovery-sent"><FaEnvelope/><span>Reset instructions sent to</span><b>{email}</b><a href="#/login">Return to sign in</a></div>:<form onSubmit={submit} className="auth-form recovery-form"><Field label="Account email" type="email" required value={email} onChange={e=>setEmail(e.target.value)} placeholder="you@company.com" autoFocus /><button className="primary wide" disabled={busy}>{busy?"Sending secure link…":"Send reset link"}</button></form>}</RecoveryShell>;
}
export function ResetPassword() {
  const token=new URLSearchParams(location.hash.split("?")[1]??"").get("token")??"";
  const [password,setPassword]=useState(""),[done,setDone]=useState(false),[error,setError]=useState("");
  const submit=async(e:React.FormEvent)=>{e.preventDefault();setError("");try{await api.resetPassword(token,password);setDone(true)}catch(e){setError((e as Error).message)}};
  return <RecoveryShell icon="lock" title={done ? "Password updated" : "Create a new password"} subtitle={done ? "Your account is secured with your new password." : "Choose a strong password with at least eight characters."}>{done?<div className="success-box">All done. <a href="#/login">Sign in with your new password</a>.</div>:<form onSubmit={submit} className="auth-form recovery-form"><PasswordField label="New password" minLength={8} required value={password} onChange={e=>setPassword(e.target.value)} autoFocus />{error&&<p className="form-error">{error}</p>}<button className="primary wide" disabled={!token}>Update password</button></form>}</RecoveryShell>;
}
export function CreateOrganization({ onCreated }: { onCreated: (slug: string) => void }) {
  const [name,setName]=useState(""),[error,setError]=useState(""),[busy,setBusy]=useState(false);
  const submit=async(e:React.FormEvent)=>{e.preventDefault();setBusy(true);try{onCreated((await api.createOrganization(name)).slug)}catch(e){setError((e as Error).message);setBusy(false)}};
  return <Shell title="Name your organization" subtitle="This is the shared home for your team, projects, and catalogs."><form onSubmit={submit} className="auth-form"><Field label="Organization name" value={name} onChange={e=>setName(e.target.value)} placeholder="Acme Media" required autoFocus />{error&&<p className="form-error">{error}</p>}<button className="primary wide" disabled={busy}>{busy?"Creating…":"Create organization"}</button></form></Shell>;
}
